import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatDate, formatTime } from "@/shared/lib/date-time";
import { getInitials } from "@/shared/lib/utils";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { CancelPackageDialog } from "@/shared/ui/CancelPackageDialog";
import { QueryState } from "@/shared/ui/QueryState";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Card,
  CardContent,
  CardTitle,
  Progress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { PrivateLessonGroupPackageBadge } from "../ui/PrivateLessonGroupPackageBadge";
import { isBefore } from "date-fns";
import {
  CalendarIcon,
  ClipboardIcon,
  EyeIcon,
  GraduationCapIcon,
  HistoryIcon,
  InfoIcon,
  PlusIcon,
  UserIcon,
  XIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import {
  useCancelPrivateLessonPackage,
  usePrivateLessonBookingsQuery,
  usePrivateLessonPackageQuery,
} from "../api/queries";
import { PrivateLessonBookingLocationCell } from "../ui/PrivateLessonBookingLocationCell";

export function PrivateLessonPackageDetailPage() {
  const nav = useNavigate({
    from: "/app/private-lessons/packages/$packageId",
  });
  const { packageId } = useParams({
    from: "/app/private-lessons/packages/$packageId",
  });
  const { data: packageData, ...result } =
    usePrivateLessonPackageQuery(packageId);
  const canUpdate = useActionPermission(["private-lessons:update"]);
  const canCreate = useActionPermission(["private-lessons:create"]);
  const cancelPackage = useCancelPrivateLessonPackage();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  // Fetch bookings for this package
  const bookingsQuery = useMemo(
    () => ({
      limit: 100,
      offset: 0,
      filters: [{ field: "packageId", op: "eq" as const, value: packageId }],
      sort: [{ field: "date", dir: "desc" as const }],
    }),
    [packageId],
  );
  const bookings = usePrivateLessonBookingsQuery(bookingsQuery);

  return (
    <>
      <CancelPackageDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        packageLabel={packageData?.templateName}
        onConfirm={async (cancelReason) => {
          await cancelPackage.mutateAsync({ id: packageId, cancelReason });
        }}
      />
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ແພັກເກດຮຽນສ່ວນຕົວ", to: "/app/private-lessons/packages" },
            { label: "ລາຍລະອຽດ" },
          ]}
        />
      </Header>

      <Main>
        {/* Header with Title, Status, and Action Buttons */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-bold text-3xl tracking-tight">
                #{packageData?.id.slice(0, 8).toUpperCase() || "Loading..."}
              </h1>
              {packageData && (
                <Badge
                  variant={
                    packageData.status === "active"
                      ? "default"
                      : packageData.status === "expired"
                        ? "destructive"
                        : "secondary"
                  }
                  className="text-xs"
                >
                  {packageData.status === "active"
                    ? "ໃຊ້ງານ"
                    : packageData.status === "expired"
                      ? "ໝົດອາຍຸ"
                      : packageData.status === "used_up"
                        ? "ໃຊ້ຄົບແລ້ວ"
                        : packageData.status === "canceled"
                          ? "ຍົກເລີກ"
                          : packageData.status}
                </Badge>
              )}
            </div>
            {packageData && (
              <p className="mt-2 text-muted-foreground text-sm">
                ແພັກເກດຮຽນສ່ວນຕົວສຳລັບ {packageData.studentName}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {packageData?.status === "active" &&
              !isBefore(packageData.expireDate, new Date()) &&
              packageData.remainingHours > 0 &&
              canCreate && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      nav({
                        to: "/app/private-lessons/bookings/bulk-create",
                        search: {
                          studentId: packageData.studentId,
                          packageId: packageData.id,
                        },
                      });
                    }}
                  >
                    ຈອງຫຼາຍຄັ້ງ
                  </Button>
                  <Button
                    onClick={() => {
                      nav({
                        to: `/app/private-lessons/bookings/create?packageId=${packageData.id}`,
                      });
                    }}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <PlusIcon className="mr-2 size-4" />
                    ສ້າງການຈອງ
                  </Button>
                </>
              )}
            {packageData?.status === "active" && canUpdate && (
              <Button
                variant="outline"
                onClick={() => setCancelDialogOpen(true)}
              >
                <XIcon className="mr-2 size-4" />
                ຍົກເລີກແພັກເກດ
              </Button>
            )}
          </div>
        </div>

        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດ package"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!packageData ? null : (
            <div className="space-y-6">
              {/* Package Info Section */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex size-8 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                    <InfoIcon className="size-4 text-red-600 dark:text-red-400" />
                  </div>
                  <CardTitle>ຂໍ້ມູນແພັກເກດ</CardTitle>
                </div>
                <div className="flex gap-4">
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-red-600 text-xs hover:text-red-700"
                    onClick={() => {
                      nav({
                        to: "/app/students/$id",
                        params: { id: packageData.studentId },
                      });
                    }}
                  >
                    <UserIcon className="mr-1 size-3" />
                    ໂປຣໄຟລ໌ນັກຮຽນ
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-red-600 text-xs hover:text-red-700"
                    onClick={() => {
                      nav({
                        to: "/app/private-lessons/templates/$id",
                        params: { id: packageData.templateId },
                      });
                    }}
                  >
                    <GraduationCapIcon className="mr-1 size-3" />
                    ລາຍລະອຽດ Template
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                {/* Student Card */}
                <div className="rounded-lg border bg-card p-4">
                  <div className="mb-3 flex items-center justify-center">
                    <div className="flex size-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                      <UserIcon className="size-5 text-red-600 dark:text-red-400" />
                    </div>
                  </div>
                  <p className="mb-1 text-center font-semibold text-muted-foreground text-xs uppercase">
                    ນັກຮຽນ
                  </p>
                  <p className="text-center font-semibold">
                    {packageData.studentName}
                  </p>
                </div>

                {/* Template Card */}
                <div className="rounded-lg border bg-card p-4">
                  <div className="mb-3 flex items-center justify-center">
                    <div className="flex size-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                      <GraduationCapIcon className="size-5 text-red-600 dark:text-red-400" />
                    </div>
                  </div>
                  <p className="mb-1 text-center font-semibold text-muted-foreground text-xs uppercase">
                    Template
                  </p>
                  <p className="text-center font-semibold">
                    {packageData.templateName}
                  </p>
                  {(packageData.packageKind === "group" ||
                    packageData.packageKind === "private_shared") && (
                    <div className="mt-2 flex justify-center">
                      <PrivateLessonGroupPackageBadge
                        packageKind={packageData.packageKind}
                        groupSize={packageData.groupSize}
                      />
                    </div>
                  )}
                  {packageData.templateDefaultLocationKind === "off_site" && (
                    <div className="mt-2 flex justify-center">
                      <Badge variant="outline">ນອກສະຖານທີ່</Badge>
                    </div>
                  )}
                </div>

                {/* Usage Card */}
                <div className="rounded-lg border bg-card p-4">
                  <div className="mb-3 flex items-center justify-center">
                    <div className="flex size-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                      <HistoryIcon className="size-5 text-red-600 dark:text-red-400" />
                    </div>
                  </div>
                  <p className="mb-1 text-center font-semibold text-muted-foreground text-xs uppercase">
                    ການໃຊ້ງານ
                  </p>
                  <p className="text-center font-semibold">
                    {packageData.totalHours - packageData.remainingHours} ຈາກ{" "}
                    {packageData.totalHours} ຊົ່ວໂມງ
                  </p>
                </div>

                {/* Validity Period Card */}
                <div className="rounded-lg border bg-card p-4">
                  <div className="mb-3 flex items-center justify-center">
                    <div className="flex size-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                      <CalendarIcon className="size-5 text-red-600 dark:text-red-400" />
                    </div>
                  </div>
                  <p className="mb-1 text-center font-semibold text-muted-foreground text-xs uppercase">
                    ໄລຍະເວລາມີຜົນບັງຄັບໃຊ້
                  </p>
                  <p className="text-center font-semibold text-sm">
                    {formatDate(packageData.startDate)} —{" "}
                    {formatDate(packageData.expireDate)}
                  </p>
                </div>
              </div>

              {/* Hours Consumption Section */}
              <Card>
                <CardContent>
                  {(() => {
                    const usedHours =
                      packageData.totalHours - packageData.remainingHours;
                    const progressPercentage =
                      packageData.totalHours > 0
                        ? (usedHours / packageData.totalHours) * 100
                        : 0;

                    return (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <CardTitle>ການໃຊ້ຊົ່ວໂມງ</CardTitle>
                          <span className="font-semibold text-sm">
                            {progressPercentage.toFixed(0)}% ສຳເລັດ
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <Progress
                            value={progressPercentage}
                            className="h-3"
                          />
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">
                            ໃຊ້ແລ້ວ {usedHours} ຊົ່ວໂມງ
                          </span>
                          <span className="font-semibold uppercase">
                            ເຫຼືອ {packageData.remainingHours} ຊົ່ວໂມງ
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Booking History */}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex size-8 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                    <ClipboardIcon className="size-4 text-red-600 dark:text-red-400" />
                  </div>
                  <CardTitle>ປະຫວັດການຈອງ</CardTitle>
                </div>
              </div>

              {bookings.isLoading ? (
                <div className="py-8 text-center text-muted-foreground">
                  ກຳລັງໂຫຼດ...
                </div>
              ) : bookings.data?.data.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  ບໍ່ມີການຈອງ
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ວັນທີ & ເວລາ</TableHead>
                        <TableHead>ລາຍການ</TableHead>
                        <TableHead>ຄູຜູ້ສອນ</TableHead>
                        <TableHead>ສະຖານທີ່</TableHead>
                        <TableHead>ສະຖານະ</TableHead>
                        <TableHead className="w-[50px]" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.data?.data.map((booking) => {
                        const status = booking.status;
                        const variant =
                          status === "scheduled"
                            ? "default"
                            : status === "completed"
                              ? "secondary"
                              : status === "cancelled"
                                ? "destructive"
                                : "outline";
                        const label =
                          status === "scheduled"
                            ? "ຈອງແລ້ວ"
                            : status === "completed"
                              ? "ເຂົ້າຮຽນແລ້ວ"
                              : status === "cancelled"
                                ? "ຍົກເລີກ"
                                : "ຮ່າງ";

                        return (
                          <TableRow key={booking.id}>
                            <TableCell>
                              <div>
                                <div className="font-semibold">
                                  {formatDate(booking.date)}
                                </div>
                                <div className="text-muted-foreground text-xs">
                                  {formatTime(booking.startTime)} -{" "}
                                  {formatTime(booking.endTime)}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="font-semibold">
                                {booking.durationMinutes} ນາທີ
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className="bg-red-100 text-red-600">
                                    {getInitials(
                                      (booking.instructorNames ?? []).join(
                                        ", ",
                                      ),
                                    )}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="font-semibold">
                                  {(booking.instructorNames ?? []).join(", ") ||
                                    "-"}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <PrivateLessonBookingLocationCell
                                locationKind={booking.locationKind}
                                offSiteLabel={booking.offSiteLabel}
                                offSiteDistance={booking.offSiteDistance}
                                roomName={booking.roomName}
                              />
                            </TableCell>
                            <TableCell>
                              <Badge variant={variant}>{label}</Badge>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  nav({
                                    to: "/app/private-lessons/bookings/$id",
                                    params: { id: booking.id },
                                  })
                                }
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Internal Administration Notes */}
              {/* <Card>
                <CardHeader>
                  <CardTitle>ບັນທຶກການບໍລິຫານພາຍໃນ</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-lg border bg-muted/50 p-4">
                    <p className="text-muted-foreground text-sm">
                      ບໍ່ມີບັນທຶກພາຍໃນ.
                    </p>
                  </div>
                </CardContent>
              </Card> */}
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
